import { useEffect, useMemo, useRef, useState } from "react";
import { T, UI, BTN } from "../constants/tokens";
import { TYPE } from "../constants/typography";
import { REGION_HE } from "../constants/regions";
import { FINNHUB_VALUATION_ENABLED } from "../constants/features";
import { Card } from "./Card";
import { usePortfolioSegments, exposureSource } from "../utils/exposureData";
import { dateNumeric } from "../utils/formatDate";
import { useIsDesktop } from "../utils/hooks";
import { noQuoteText } from "../utils/quoteState";
import {
  applyScenario, measure, compare, lookupSymbol, newRow, targetShares, takePendingSymbol,
  EMPTY_STATE, heldOrder, heldValue, heldChanged, todayValue, scenarioRows, switchUnit, isPristine, unconvertible,
  readState, writeState,
} from "../utils/scenario";
import SymbolAutocomplete from "./SymbolAutocomplete";
import { Loading, Skeleton } from "./Skeleton";
import DualMoney, { shekelValue } from "./DualMoney";
import { useFxRates } from "../utils/fxRates";
import Icon from "./Icon";
import LTR from "./LTR";
import { Amount as MoneyAmount } from "./Money";

// ─── "מה אם" · the portfolio before and after a change (sprint 14, stage 1) ──
// The screen opens on the reader's own portfolio: one row per holding at
// today's quantity, and a blank row for another symbol. The result is there
// from the first frame · "התיק כמו היום" · and follows every keystroke. The
// reader only changes a number (David, 06/10/2026: plan approved, with one
// unit selector for the screen, a result bar on the phone, and the five
// breakdowns open from the start).
//
// It does nothing and says nothing about what to do: no buy, no sell, no
// colour that reads as better or worse. An arrow says which way a number
// moved, in one neutral ink. The before and after come from the functions of
// "חשיפות התיק" (utils/scenario.js), so the two screens cannot disagree.

const INTRO = "שנה כמות או הוסף נייר, וראה איך משתנה הרכב התיק";
const FOOT = "לא מבצע פעולה · מידע בלבד · אינו ייעוץ השקעות";
const PLACE_HE = { ...REGION_HE, other: "אזורים אחרים" };

const pct1 = v => `${(v || 0).toFixed(1)}%`;
// Dollars through the shared Amount (sprint 17), at the size of the text
// around them; counts and shares as text.
const Usd = ({ v }) => <MoneyAmount value={v || 0} currency="USD" decimals={0} size={null} weight={null}/>;
const shown = (l, v) => (l.kind === "usd" ? <Usd v={v}/> : l.kind === "count" ? String(Math.round(v || 0)) : pct1(v));
const qty = n => (Number.isInteger(n) ? n.toLocaleString("en-US") : n.toLocaleString("en-US", { maximumFractionDigits: 4 }));
const clock = ms => {
  const d = new Date(ms);
  return `${dateNumeric(d, { year: false })} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};
const priceOf = h => (h && h.quote !== "none" && h.currentPrice > 0 ? h.currentPrice : null);
const SYM = s => String(s || "").trim().toUpperCase();
const num = v => Number(String(v).replace(/,/g, "")) || 0;

// ── One line: label · from → to · the direction ─────────────────────────────
// With `single`, the screen is still today's portfolio: one figure, no arrow.
function DiffLine({ l, label, single }) {
  const up = (l.after || 0) > (l.before || 0);
  const tag = t => (t ? <><LTR style={{ fontWeight: 800 }}>{t}</LTR> </> : null);
  // An amount carries its shekels under it (sprint 15, DualMoney): one value
  // for today, both sides for before and after.
  const usdLine = l.kind === "usd";
  const amount = (v, w) => <LTR className="si-num" style={{ fontWeight: w, color: w === 800 ? UI.text.primary : undefined }}>{shown(l, v)}</LTR>;
  return (
    <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 10, padding: "9px 0", borderBottom: `1px solid ${UI.line.hairline}`, flexWrap: "wrap" }}>
      <span style={{ fontSize: 14, fontWeight: 700, color: l.muted ? UI.text.secondary : UI.text.primary, minWidth: 0 }}>
        {label ?? (l.ltr ? <LTR>{l.label}</LTR> : l.label)}
      </span>
      {single && usdLine ? (
        <DualMoney usd={l.after} cur="USD" size={14} style={{ alignItems: "flex-end" }}>{amount(l.after, 800)}</DualMoney>
      ) : !single && usdLine ? (
        <span style={{ display: "inline-flex", flexDirection: "column", alignItems: "flex-end", gap: 2 }}>
          <span style={{ fontSize: 14, color: UI.text.secondary, whiteSpace: "nowrap" }}>
            מ-{amount(l.before, 600)} ל-{amount(l.after, 800)}
            {l.changed && (l.after || 0) !== (l.before || 0) && (
              <span role="img" aria-label={up ? "עלה" : "ירד"} style={{ display: "inline-block", fontWeight: 800, color: UI.text.secondary, minWidth: 12, marginInlineStart: 6, textAlign: "center" }}>{up ? "↑" : "↓"}</span>
            )}
          </span>
          <ShekelPair before={l.before} after={l.after}/>
        </span>
      ) : single ? (
        <span style={{ fontSize: 14, color: UI.text.secondary, whiteSpace: "nowrap" }}>
          {tag(l.afterTag)}<LTR className="si-num" style={{ fontWeight: 800, color: UI.text.primary }}>{shown(l, l.after)}</LTR>
        </span>
      ) : (
        <span style={{ fontSize: 14, color: UI.text.secondary, whiteSpace: "nowrap" }}>
          מ-{tag(l.beforeTag)}<LTR className="si-num" style={{ fontWeight: 600 }}>{shown(l, l.before)}</LTR>
          {" "}ל-{tag(l.afterTag)}<LTR className="si-num" style={{ fontWeight: 800, color: UI.text.primary }}>{shown(l, l.after)}</LTR>
          {l.changed && (l.after || 0) !== (l.before || 0) && (
            <span role="img" aria-label={up ? "עלה" : "ירד"} style={{ display: "inline-block", fontWeight: 800, color: UI.text.secondary, minWidth: 12, marginInlineStart: 6, textAlign: "center" }}>{up ? "↑" : "↓"}</span>
          )}
        </span>
      )}
    </div>
  );
}

// The shekels of a before-and-after amount, at today's rate, in one line
// under the dollars: "מ-₪… ל-₪…". The rate is in the tooltip, as in DualMoney.
function ShekelPair({ before, after }) {
  const fx = useFxRates();
  const b = shekelValue(before, { fx }), a = shekelValue(after, { fx });
  if (b == null || a == null) return null;
  const tip = `לפי שער ${fx.ILS.rate.toFixed(3)} ₪ לדולר${fx.ILS.source ? ` · מקור: ${fx.ILS.source}` : ""}`;
  return (
    <span data-shekels title={tip} style={{ fontSize: 13, fontWeight: 700, color: UI.text.primary, whiteSpace: "nowrap" }}>
      מ-<MoneyAmount value={b} currency="ILS" size={null} weight={null}/> ל-<MoneyAmount value={a} currency="ILS" size={null} weight={null}/>
    </span>
  );
}

// After a change: the lines that moved first, the rest under one quiet
// toggle. Before any change every line is today's figure, all open.
function Lines({ lines, labelOf, single }) {
  const [open, setOpen] = useState(false);
  if (single) return <div>{lines.map(l => <DiffLine key={l.key} l={l} label={labelOf?.(l)} single/>)}</div>;
  const moved = lines.filter(l => l.changed);
  const still = lines.filter(l => !l.changed);
  return (
    <div>
      {moved.map(l => <DiffLine key={l.key} l={l} label={labelOf?.(l)}/>)}
      {moved.length === 0 && <div style={{ ...TYPE.bodySmall, padding: "6px 0" }}>ללא שינוי</div>}
      {still.length > 0 && moved.length > 0 && (
        <button onClick={() => setOpen(o => !o)} aria-expanded={open} style={LINK_BTN}>
          {open ? "הסתר" : still.length === 1 ? "ללא שינוי בשורה אחת" : <>ללא שינוי ב-<LTR className="si-num">{still.length}</LTR> שורות</>}
        </button>
      )}
      {open && still.map(l => <DiffLine key={l.key} l={l} label={labelOf?.(l)}/>)}
    </div>
  );
}

function Section({ n, title, basis, source, children }) {
  return (
    <Card style={{ padding: "18px 20px 16px" }}>
      <h2 style={{ ...TYPE.heading, margin: 0, display: "flex", alignItems: "baseline", gap: 8 }}>
        {n && <LTR className="si-num" style={{ fontSize: 13, color: UI.text.muted, fontWeight: 700 }}>{n}</LTR>}
        {title}
      </h2>
      {basis && <div style={{ ...TYPE.caption, marginTop: 6, fontSize: 12.5 }}>{basis}</div>}
      <div style={{ marginTop: T.scale[2] }}>{children}</div>
      {source && <div style={{ ...TYPE.caption, marginTop: T.scale[3], paddingTop: T.scale[2], borderTop: `1px solid ${UI.line.hairline}` }}>{source}</div>}
    </Card>
  );
}

const INPUT = {
  width: "100%", boxSizing: "border-box", minHeight: 44, padding: "8px 12px", borderRadius: T.radius.md,
  border: `1px solid ${UI.line.strong}`, background: UI.surface.sunken, color: UI.text.primary,
  fontSize: 15, fontFamily: "inherit",
};

const LINK_BTN = {
  background: "transparent", border: "none", padding: "10px 0 2px", minHeight: 44,
  fontSize: 13.5, fontWeight: 700, color: UI.interactive, cursor: "pointer", fontFamily: "inherit",
};

const unitWord = n => (n === 1 ? "יחידה" : "יחידות");
const figure = (unit, v) => (unit === "usd"
  ? <Usd v={num(v)}/>
  : <><LTR className="si-num">{qty(num(v))}</LTR> {unitWord(num(v))}</>);

// The number field of a row, with its hint while it has the focus.
function Amount({ value, unit, onChange, disabled, inputRef, label, onFocus, onBlur, focused, hintId }) {
  return (
    <div style={{ minWidth: 0 }}>
      <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 13.5, fontWeight: 700, color: UI.text.secondary, whiteSpace: "nowrap" }}>{unit === "usd" ? "שווי $" : "כמות"}</span>
        <input ref={inputRef} inputMode="decimal" dir="ltr" value={value} disabled={disabled} aria-label={label} aria-describedby={hintId}
          onChange={e => onChange(e.target.value.replace(/[^\d.,]/g, ""))} onBlur={onBlur}
          // The whole figure is selected on focus: typing replaces today's
          // quantity instead of appending to it, on a phone as on a keyboard.
          onFocus={e => { const el = e.target; onFocus(); requestAnimationFrame(() => { try { el.select(); } catch {} }); }}
          style={{ ...INPUT, flex: 1, minWidth: 0, opacity: disabled ? 0.55 : 1 }}/>
      </label>
      {focused && !disabled && <div id={hintId} style={{ ...TYPE.caption, marginTop: 4 }}><LTR className="si-num">0</LTR> מוציא את הנייר</div>}
    </div>
  );
}

// ── A holding's row ─────────────────────────────────────────────────────────
function HeldRow({ h, state, onValue, onReset, highlighted, inputRef, focused, setFocused }) {
  const price = priceOf(h);
  const value = heldValue(state, h);
  const changed = heldChanged(state, h);
  const today = todayValue(h, state.unit);
  const target = price ? targetShares({ unit: state.unit, value }, price) : null;
  return (
    <div data-scenario-row={h.symbol} data-changed={changed ? "1" : "0"} data-highlighted={highlighted ? "1" : "0"}
      style={{ padding: "12px 12px", margin: "0 -12px", borderBottom: `1px solid ${UI.line.hairline}`,
        borderInlineStart: `3px solid ${changed ? UI.interactive : "transparent"}`,
        background: highlighted ? UI.fill.subtle : "transparent", transition: "background 200ms" }}>
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 0.9fr) minmax(0, 1.1fr)", gap: 10, alignItems: "center" }}>
        <div style={{ minWidth: 0 }}>
          <LTR style={{ fontWeight: 800, fontSize: 15, color: UI.text.primary }}>{h.symbol}</LTR>
          {h.name && h.name !== h.symbol && <div style={{ ...TYPE.caption, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{h.name}</div>}
        </div>
        <Amount value={value} unit={state.unit} disabled={!price} inputRef={inputRef} label={`${state.unit === "usd" ? "שווי" : "כמות"} בתרחיש · ${h.symbol}`} hintId={`hint-${h.symbol}`}
          onChange={onValue} focused={focused} onFocus={() => setFocused(h.symbol)} onBlur={() => setFocused(null)}/>
      </div>
      <div style={{ ...TYPE.caption, marginTop: 6, lineHeight: 1.6 }}>
        {!price ? (
          <>{noQuoteText(h)} · השורה אינה בחישוב</>
        ) : changed ? (
          <span style={{ display: "inline-flex", flexWrap: "wrap", alignItems: "baseline", gap: 8 }}>
            <span style={{ color: UI.text.primary, fontWeight: 700 }}>
              מ-{figure(state.unit, today)} ל-{figure(state.unit, value)}
              {target === 0 && <> · אינו בתרחיש</>}
            </span>
            <button onClick={onReset} aria-label={`חזרה לכמות של היום · ${h.symbol}`} style={{ ...LINK_BTN, padding: 0, minHeight: 0, fontSize: 12.5 }}>
              חזרה ל-{state.unit === "usd" ? <Usd v={num(today)}/> : <LTR className="si-num">{qty(h.shares)}</LTR>}
            </button>
          </span>
        ) : (
          <>היום בתיק · {state.unit === "usd"
            ? <><LTR className="si-num">{qty(h.shares)}</LTR> {unitWord(h.shares)}</>
            : <Usd v={h.shares * price}/>}</>
        )}
      </div>
    </div>
  );
}

// ── A row for another symbol ────────────────────────────────────────────────
function ExtraRow({ row, state, look, duplicate, onChange, onRemove, onRetry, blank, inputRef, focused, setFocused }) {
  const sym = SYM(row.symbol);
  const price = look && !look.error ? look.price : null;
  const target = price ? targetShares({ unit: state.unit, value: row.value }, price) : null;
  return (
    <div data-scenario-row={sym || "empty"} style={{ padding: "12px 0", borderBottom: `1px solid ${UI.line.hairline}` }}>
      {blank && <div style={{ fontSize: 13.5, fontWeight: 700, color: UI.text.secondary, marginBottom: 6 }}>נייר נוסף</div>}
      <div style={{ display: "grid", gridTemplateColumns: blank ? "minmax(0, 0.9fr) minmax(0, 1.1fr)" : "minmax(0, 0.9fr) minmax(0, 1.1fr) 44px", gap: 10, alignItems: "end" }}>
        <SymbolAutocomplete value={row.symbol} onChange={v => onChange({ ...row, symbol: v })} label="סימול" placeholder="למשל NVDA" inputStyle={INPUT}/>
        <Amount value={row.value} unit={state.unit} inputRef={inputRef} label={`${state.unit === "usd" ? "שווי" : "כמות"} בתרחיש · ${sym || "נייר נוסף"}`} hintId={`hint-${row.id}`}
          onChange={v => onChange({ ...row, value: v })} focused={focused} onFocus={() => setFocused(row.id)} onBlur={() => setFocused(null)}/>
        {!blank && (
          <button onClick={onRemove} aria-label="הסר שורה"
            style={{ background: "transparent", border: `1px solid ${UI.line.hairline}`, borderRadius: T.radius.md, width: 44, height: 44, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
            <Icon name="x" size={14} color={UI.text.secondary}/>
          </button>
        )}
      </div>
      {sym && (
        <div style={{ ...TYPE.caption, marginTop: 6, lineHeight: 1.6 }}>
          {!look ? "טוען מחיר…"
            : look.error === "no_price" ? <>אין מחיר ל-<LTR>{sym}</LTR> במקור הנתונים · השורה אינה בחישוב</>
            : look.error ? <>המחיר של <LTR>{sym}</LTR> לא נטען · השורה אינה בחישוב · <button onClick={onRetry} style={{ ...LINK_BTN, padding: 0, minHeight: 0, fontSize: 12.5 }}>נסה שוב</button></>
            : <>לא בתיק היום · מחיר אחרון <Usd v={look.price}/> · <LTR>Finnhub</LTR></>}
          {price && String(row.value).trim() !== "" && target == null && <> · הערך אינו מספר · השורה אינה בחישוב</>}
          {duplicate && <> · הסימול מופיע גם בשורה אחרת, ונספרת השורה האחרונה</>}
          {!duplicate && price && target != null && state.unit === "usd" && <> · בתרחיש <LTR className="si-num">{qty(Math.round(target * 10000) / 10000)}</LTR> יחידות</>}
          {!duplicate && price && target != null && state.unit === "shares" && <> · בתרחיש <Usd v={target * price}/></>}
        </div>
      )}
    </div>
  );
}

export default function ScenarioTab({ portfolio = [], portfolioReady = true }) {
  const isDesktop = useIsDesktop();
  const [state, setState] = useState(() => readState() || EMPTY_STATE);
  const [lookups, setLookups] = useState({});
  const [focused, setFocused] = useState(null);
  const [highlight, setHighlight] = useState(null);
  const [focusReq, setFocusReq] = useState(null);   // { key, select }
  const inputs = useRef({});
  const summaryRef = useRef(null);
  const stateRef = useRef(state);
  stateRef.current = state;
  useEffect(() => { writeState(state); }, [state]);

  const held = useMemo(() => heldOrder(portfolio), [portfolio]);
  const heldOf = sym => portfolio.find(p => p.symbol === sym && p.shares > 0) || null;

  // Today's portfolio decides which overrides still mean something · once it
  // has loaded. Before that the list is empty, and pruning against it erased
  // every change on a reload (review).
  useEffect(() => {
    if (!portfolioReady) return;
    setState(prev => {
      const keep = Object.fromEntries(Object.entries(prev.overrides).filter(([s]) => held.some(h => h.symbol === s)));
      return Object.keys(keep).length === Object.keys(prev.overrides).length ? prev : { ...prev, overrides: keep };
    });
  }, [held, portfolioReady]);

  // The rows for other symbols, and always one blank at the end.
  const extras = state.extras;
  const last = extras[extras.length - 1];
  const lastBlank = !!last && !SYM(last.symbol) && !String(last.value).trim();
  const blankRow = useMemo(() => newRow(), [extras.length]); // eslint-disable-line react-hooks/exhaustive-deps
  const shownExtras = lastBlank ? extras : [...extras, blankRow];

  const setExtra = next => setState(prev => {
    const has = prev.extras.some(x => x.id === next.id);
    return { ...prev, extras: has ? prev.extras.map(x => (x.id === next.id ? next : x)) : [...prev.extras, next] };
  });
  const removeExtra = id => setState(prev => ({ ...prev, extras: prev.extras.filter(x => x.id !== id) }));
  const setOverride = (sym, v) => { setHighlight(h => (h && h !== sym ? null : h)); setState(prev => ({ ...prev, overrides: { ...prev.overrides, [sym]: v } })); };
  const resetHeld = sym => setState(prev => { const o = { ...prev.overrides }; delete o[sym]; return { ...prev, overrides: o }; });
  const resetAll = () => { setHighlight(null); setState(prev => ({ ...EMPTY_STATE, unit: prev.unit })); };

  // The company page's "בדוק בתרחיש": a held company's row is highlighted and
  // focused; another company goes into the blank row, with no quantity.
  // Before the portfolio has loaded, a held company would look like a new
  // one: the symbol waits (pendingEntry) until it has (review).
  const pendingEntry = useRef(null);
  useEffect(() => {
    const take = () => {
      const s = takePendingSymbol() || pendingEntry.current;
      if (!s) return;
      if (!portfolioReady) { pendingEntry.current = s; return; }
      pendingEntry.current = null;
      if (portfolio.some(p => p.symbol === s && p.shares > 0)) {
        setHighlight(s);
        setFocusReq({ key: s, select: true });
        return;
      }
      // Decided on the current state, outside the updater: an updater may
      // run twice, and a new row's id must be the one that is focused.
      const cur = stateRef.current;
      const existing = cur.extras.find(x => SYM(x.symbol) === s);
      if (existing) { setFocusReq({ key: existing.id }); return; }
      const blank = cur.extras.find(x => !SYM(x.symbol) && !String(x.value).trim());
      const row = blank ? { ...blank, symbol: s } : newRow(s, "");
      setFocusReq({ key: row.id });
      setState(prev => ({ ...prev, extras: blank ? prev.extras.map(x => (x.id === blank.id ? row : x)) : [...prev.extras, row] }));
    };
    take();
    window.addEventListener("si-open-scenario", take);
    return () => window.removeEventListener("si-open-scenario", take);
  }, [portfolio, portfolioReady]);

  // Focus once the row is on screen. A new row appears in the render after
  // the state that adds it, so the rows are a dependency too.
  useEffect(() => {
    if (!focusReq) return;
    const el = inputs.current[focusReq.key];
    if (!el) return;
    el.scrollIntoView({ block: "center", behavior: "smooth" });
    el.focus({ preventScroll: true });
    if (focusReq.select) el.select();
    setFocusReq(null);
  }, [focusReq, state.extras, held]);

  // The price and sector of a symbol that is not held · once per symbol,
  // after the reader stops typing. A failed lookup is not kept: "נסה שוב"
  // drops it, and the next pass asks again.
  const wanted = extras.map(x => SYM(x.symbol)).filter(s => s && !heldOf(s) && !(s in lookups));
  const retry = s => setLookups(prev => { const next = { ...prev }; delete next[s]; return next; });
  const wantedKey = [...new Set(wanted)].sort().join(",");
  useEffect(() => {
    if (!wantedKey) return undefined;
    const t = setTimeout(() => {
      for (const s of wantedKey.split(",")) {
        lookupSymbol(s).then(res => setLookups(prev => (s in prev ? prev : { ...prev, [s]: res })));
      }
    }, 600);
    return () => clearTimeout(t);
  }, [wantedKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = useMemo(() => scenarioRows(portfolio, state), [portfolio, state]);
  const after = useMemo(() => applyScenario(portfolio, rows, lookups), [portfolio, rows, lookups]);
  const pristine = isPristine(state, portfolio);

  const priced = Object.keys(lookups).filter(s => !lookups[s].error);
  const symbols = useMemo(() => [...new Set([...portfolio.map(p => p.symbol), ...priced])], [portfolio, priced.join(",")]); // eslint-disable-line react-hooks/exhaustive-deps
  const segNow = usePortfolioSegments(symbols);
  const lastSeg = useRef(null);
  if (segNow) lastSeg.current = segNow;
  const seg = segNow || lastSeg.current;
  const cmp = useMemo(() => {
    const b = measure(portfolio, seg?.segmentsBySymbol);
    const a = measure(after, seg?.segmentsBySymbol);
    return b.base.total > 0 || a.base.total > 0 ? compare(b, a) : null;
  }, [portfolio, after, seg]);
  const src = seg ? exposureSource(seg.segmentsBySymbol) : null;
  // "Before and after" once a row actually moves a number; until then the
  // screen is today's portfolio.
  // Whenever a row is marked as changed, the answer is "before and after"
  // · even when the change is too small to move a figure on screen (review).
  // Only rows with a price move anything: a symbol still loading, or answered
  // without a price, leaves the answer at "today" (review).
  const pricedRows = rows.filter(r => {
    const s = SYM(r.symbol);
    const h = heldOf(s);
    return h ? !!priceOf(h) : !!(lookups[s] && !lookups[s].error);
  });
  const applied = !pristine && pricedRows.length > 0 && !!cmp;
  const single = !applied;
  const stamps = [...portfolio, ...after].map(p => p.quoteAt).filter(Boolean).map(t => (t < 1e12 ? t * 1000 : t));
  const asOf = stamps.length ? Math.min(...stamps) : null;

  const priceSource = <>מקור: מחיר אחרון מ-<LTR>Finnhub</LTR> × הכמות{asOf ? <> · מחירים נכון ל-<LTR className="si-num">{clock(asOf)}</LTR></> : null}</>;
  const largest = cmp?.conc.find(l => l.key === "largest");
  const count = cmp?.conc.find(l => l.key === "count");

  // ── The answer at the top: today's portfolio, or before and after ──────────
  const summary = cmp ? (
    <div ref={summaryRef} data-scenario-summary={single ? "today" : "diff"}>
      <Card style={{ padding: "18px 20px 14px" }}>
        <h2 style={{ ...TYPE.heading, margin: 0 }}>{single ? "התיק כמו היום" : "לפני ואחרי"}</h2>
        <div style={{ marginTop: T.scale[2] }}>
          {/* A line that did not move reads as one figure, not "מ-2 ל-2". */}
          {cmp.value.map(l => <DiffLine key={l.key} l={l} single={single || !l.changed}/>)}
          {largest && <DiffLine l={largest} single={single || !largest.changed}/>}
          {count && <DiffLine l={count} single={single || !count.changed}/>}
        </div>
        <div style={{ ...TYPE.caption, marginTop: T.scale[2] }}>{priceSource}</div>
      </Card>
    </div>
  ) : null;

  const sections = cmp ? (
    <>
      <Section n="1" title="ריכוזיות" basis="כל אחוז הוא מתוך שווי התיק באותו צד" source={priceSource}>
        <Lines single={single} lines={cmp.conc.filter(l => l.key === "top5")} labelOf={l => (l.key === "top5" ? <><LTR className="si-num">5</LTR> ההחזקות הגדולות</> : null)}/>
        <div style={{ ...TYPE.caption, marginTop: 12, marginBottom: 2 }}>משקל כל החזקה</div>
        <Lines single={single} lines={cmp.holdings}/>
      </Section>
      <Section n="2" title="סקטורים" basis="חלוקת שווי התיק לפי הסקטור של כל החזקה"
        source={<>מקור: הענף של כל חברה ב-<LTR>Finnhub</LTR>, ממופה לסקטורים של סוריקטה</>}>
        <Lines single={single} lines={cmp.sectors}/>
      </Section>
      <Section n="3" title="מטבע" source={<>מקור: ציטוטי <LTR>Finnhub</LTR> מהבורסות בארה"ב</>}>
        <Lines single={single} lines={cmp.currency} labelOf={l => (l.label === "USD" ? <>דולר (<LTR>USD</LTR>)</> : null)}/>
      </Section>
      <Section n="4" title="הכנסות לפי אזור" basis="לפי דיווחי החברות ל-SEC · משוקלל לפי שווי ההחזקה · מתוך שווי התיק"
        source={<>מקור: <LTR>SEC EDGAR</LTR> · הדוח השנתי{src?.filed ? <> · ההגשה האחרונה שנכללה <LTR className="si-num">{dateNumeric(src.filed)}</LTR></> : null}</>}>
        {!cmp.regions
          ? <Loading>{[1, 2, 3].map(i => <Skeleton key={i} h={28} mb={8}/>)}</Loading>
          : <Lines single={single} lines={cmp.regions} labelOf={l => (l.region ? PLACE_HE[l.label] || l.label : null)}/>}
      </Section>
      <Section n="5" title="מה אנחנו עדיין לא יודעים" source="פער במידע, לא הערכה של ההחזקה">
        {single || (cmp.gapsAdded.length === 0 && cmp.gapsRemoved.length === 0) ? (
          <div style={{ ...TYPE.bodySmall }}>
            {cmp.gapsAfter.length === 0 ? "לכל ההחזקות יש ציטוט מחיר, סקטור ופילוח הכנסות לפי אזור." : <><LTR className="si-num">{cmp.gapsAfter.length}</LTR> החזקות עם מידע חסר{single ? "" : ", כמו היום"}</>}
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8, fontSize: 14, lineHeight: 1.7, color: UI.text.secondary }}>
            {cmp.gapsAdded.map(g => (
              <div key={`a-${g.symbol}`}><LTR style={{ fontWeight: 800, color: UI.text.primary }}>{g.symbol}</LTR> נכנסת לתרחיש עם מידע חסר: {g.items.map(it => (typeof it === "string" ? it : <><LTR className="si-num">{pct1(it.unspecified)}</LTR> מהכנסותיה לא שויכו לאזור</>)).reduce((acc, x, i) => (i ? [...acc, " · ", x] : [x]), [])}</div>
            ))}
            {cmp.gapsRemoved.map(g => (
              <div key={`r-${g.symbol}`}><LTR style={{ fontWeight: 800, color: UI.text.primary }}>{g.symbol}</LTR> אינה בתרחיש, ולכן המידע החסר עליה אינו בחישוב</div>
            ))}
          </div>
        )}
      </Section>
      {/* Reserved: P/E, dividend and Beta, before and after, once counsel has
          answered on the Finnhub licence (ROADMAP, "לעו״ד"). Nothing renders
          and nothing on screen refers to it while the flag is off. */}
      {FINNHUB_VALUATION_ENABLED && null}
    </>
  ) : null;

  const waiting = unconvertible(state, lookups, portfolio);
  const unitBtn = (u, text) => (
    <button onClick={() => { if (!waiting.length) setState(prev => switchUnit(prev, u, portfolio, lookups)); }} aria-pressed={state.unit === u}
      aria-disabled={waiting.length > 0 && state.unit !== u}
      style={{ border: "none", borderRadius: T.radius.md, padding: "8px 14px", minHeight: 44, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
        background: state.unit === u ? UI.surface.raised : "transparent", color: state.unit === u ? UI.text.primary : UI.text.secondary,
        boxShadow: state.unit === u ? UI.shadow.card : "none",
        ...(waiting.length > 0 && state.unit !== u ? { opacity: 0.45, cursor: "default" } : null) }}>
      {text}
    </button>
  );

  // The row that counts is the last one with that symbol AND a value.
  const dupOf = (row, i) => { const s = SYM(row.symbol); return !!s && shownExtras.some((x, j) => j > i && SYM(x.symbol) === s && String(x.value).trim() !== ""); };

  const editor = (
    <Card style={{ padding: "16px 20px 18px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
        <div role="group" aria-label="הזנה ב" style={{ display: "inline-flex", alignItems: "center", gap: 2, padding: 3, borderRadius: T.radius.lg, background: UI.fill.subtle }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: UI.text.secondary, padding: "0 8px" }}>הזנה ב:</span>
          {unitBtn("shares", "יחידות")}
          {unitBtn("usd", "דולרים")}
        </div>
        {waiting.length > 0 && (
          <div style={{ ...TYPE.caption, flexBasis: "100%" }}>
            אין עדיין מחיר ל-{waiting.map((w, i) => <span key={w}>{i ? ", " : ""}<LTR>{w}</LTR></span>)} · החלפת היחידה תתאפשר כשיגיע מחיר
          </div>
        )}
        <button onClick={resetAll} disabled={pristine}
          style={{ ...BTN.secondary, minHeight: 44, padding: "8px 14px", opacity: pristine ? 0.45 : 1, cursor: pristine ? "default" : "pointer" }}>
          חזרה לתיק של היום
        </button>
      </div>
      <div style={{ marginTop: 10 }}>
        {held.map(h => (
          <HeldRow key={h.symbol} h={h} state={state} highlighted={highlight === h.symbol}
            inputRef={el => { inputs.current[h.symbol] = el; }} focused={focused === h.symbol} setFocused={setFocused}
            onValue={v => setOverride(h.symbol, v)} onReset={() => resetHeld(h.symbol)}/>
        ))}
        {shownExtras.map((x, i) => {
          const s = SYM(x.symbol);
          const isBlank = i === shownExtras.length - 1 && !s && !String(x.value).trim();
          // A held symbol typed into an extra row belongs to that holding.
          if (s && heldOf(s)) {
            return (
              <div key={x.id} style={{ ...TYPE.caption, padding: "10px 0", borderBottom: `1px solid ${UI.line.hairline}` }}>
                <LTR>{s}</LTR> כבר בתיק · הכמות שלו נקבעת בשורה שלו למעלה ·{" "}
                <button onClick={() => { removeExtra(x.id); setHighlight(s); setFocusReq({ key: s, select: true }); }} style={{ ...LINK_BTN, padding: 0, minHeight: 0, fontSize: 12.5 }}>לשורה</button>
              </div>
            );
          }
          return (
            <ExtraRow key={x.id} row={x} state={state} look={lookups[s]} duplicate={dupOf(x, i)} blank={isBlank}
              inputRef={el => { inputs.current[x.id] = el; }} focused={focused === x.id} setFocused={setFocused}
              onChange={setExtra} onRemove={() => removeExtra(x.id)} onRetry={() => retry(s)}/>
          );
        })}
      </div>
      <div style={{ ...TYPE.caption, marginTop: 10 }}>התרחיש נשמר במכשיר הזה בלבד, עד סגירת הלשונית. הוא לא נשלח לשרת.</div>
    </Card>
  );

  const nothing = !portfolioReady ? (
    <Card style={{ padding: 20 }}>
      <Loading><Skeleton w="40%" h={18} mb={10}/><Skeleton h={14} mb={8}/><Skeleton w="70%" h={14}/></Loading>
    </Card>
  ) : (
    <Card style={{ padding: 20 }}>
      <div style={{ ...TYPE.bodySmall }}>אין החזקות בתיק · נייר שיוזן בשורה "נייר נוסף" יופיע כאן.</div>
    </Card>
  );

  // The phone's result bar (approved): the largest holding before and after,
  // only once something changed, and a way back up to the full answer.
  const bar = !isDesktop && applied && largest ? (
    <div data-scenario-bar style={{ position: "sticky", bottom: 0, zIndex: 5, margin: "0 -16px", padding: "10px 16px calc(10px + env(safe-area-inset-bottom))",
      background: UI.surface.raised, borderTop: `1px solid ${UI.line.strong}`, boxShadow: UI.shadow.card,
      display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
      <span style={{ fontSize: 13.5, color: UI.text.secondary, minWidth: 0 }}>
        ההחזקה הגדולה: מ-{largest.beforeTag && <><LTR style={{ fontWeight: 800 }}>{largest.beforeTag}</LTR> </>}<LTR className="si-num">{pct1(largest.before)}</LTR>
        {" "}ל-{largest.afterTag && <><LTR style={{ fontWeight: 800 }}>{largest.afterTag}</LTR> </>}<LTR className="si-num" style={{ fontWeight: 800, color: UI.text.primary }}>{pct1(largest.after)}</LTR>
      </span>
      <button onClick={() => summaryRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
        style={{ ...LINK_BTN, padding: "0 4px", whiteSpace: "nowrap" }}>לתוצאה</button>
    </div>
  ) : null;

  return (
    <div dir="rtl" style={{ display: "flex", flexDirection: "column", gap: T.space.md, maxWidth: isDesktop ? 1120 : 680, margin: "0 auto" }}>
      <div>
        <div style={TYPE.h1}>מה אם</div>
        <div style={{ ...TYPE.bodySmall, marginTop: 6, lineHeight: 1.7 }}>{INTRO}</div>
        <div style={{ ...TYPE.caption, marginTop: 4 }}>{FOOT}</div>
      </div>
      {isDesktop ? (
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1.25fr)", gap: 24, alignItems: "start" }}>
          {editor}
          {/* The answer stays in view while the reader edits a long list. */}
          <div style={{ position: "sticky", top: 16, maxHeight: "calc(100dvh - 32px)", overflowY: "auto", display: "flex", flexDirection: "column", gap: T.space.md, paddingBottom: 4 }}>
            {summary || nothing}
            {sections}
          </div>
        </div>
      ) : (
        <>
          {summary || nothing}
          {editor}
          {sections}
          {bar}
        </>
      )}
    </div>
  );
}
